import { useEffect, useMemo, useState } from "react";
import {
  blockAdminCustomer,
  getAdminCustomers,
  unblockAdminCustomer,
} from "../../../api/adminCustomers";

const useAdminCustomers = ({ showFeedback, refreshAudit }) => {
  const [customers, setCustomers] = useState([]);
  const [customersTotal, setCustomersTotal] = useState(0);
  const [customersPage, setCustomersPage] = useState(1);
  const [customersPageSize] = useState(10);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState("");
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const [customerAppliedQuery, setCustomerAppliedQuery] = useState("");
  const [customerSortBy, setCustomerSortBy] = useState("latest");
  const [customerSortDir, setCustomerSortDir] = useState("desc");
  const [expandedCustomerKey, setExpandedCustomerKey] = useState(null);
  const [customerActionId, setCustomerActionId] = useState(null);
  const [customerBlockReasons, setCustomerBlockReasons] = useState({});

  useEffect(() => {
    let cancelled = false;

    const loadCustomers = async () => {
      setCustomersLoading(true);
      setCustomersError("");
      try {
        const payload = await getAdminCustomers({
          q: customerAppliedQuery || undefined,
          sortBy: customerSortBy,
          sortDir: customerSortDir,
          page: customersPage,
          pageSize: customersPageSize,
        });
        if (cancelled) return;
        const items = Array.isArray(payload?.items) ? payload.items : [];
        setCustomers(items);
        setCustomersTotal(payload?.totalCount ?? items.length);
      } catch (error) {
        if (cancelled) return;
        setCustomersError(error?.message || "Failed to load customers.");
        setCustomers([]);
        setCustomersTotal(0);
      } finally {
        if (!cancelled) setCustomersLoading(false);
      }
    };

    loadCustomers();

    return () => {
      cancelled = true;
    };
  }, [customerAppliedQuery, customerSortBy, customerSortDir, customersPage, customersPageSize]);

  useEffect(() => {
    if (!customers.length) return;
    setCustomerBlockReasons((prev) => {
      const next = { ...prev };
      customers.forEach((customer) => {
        if (next[customer.key] === undefined) {
          next[customer.key] = customer.blockReason || "";
        }
      });
      return next;
    });
  }, [customers]);

  const totalCustomerPages = Math.max(1, Math.ceil(customersTotal / customersPageSize));

  const customerProfiles = useMemo(
    () =>
      customers.map((customer) => ({
        key: customer.key || customer.email,
        name: customer.name || customer.email || "Customer",
        email: customer.email || "",
        phone: customer.phone || "",
        accountCreatedUtc: customer.accountCreatedUtc || null,
        bookingCount: customer.bookingCount ?? 0,
        totalSpent: customer.totalSpent ?? 0,
        refundedTotal: customer.refundedTotal ?? 0,
        noShowCount: customer.noShowCount ?? 0,
        latestBookingId: customer.latestBookingId,
        latestBookingUtc: customer.latestBookingUtc,
        currency: customer.currency || "EUR",
        isBlocked: Boolean(customer.isBlocked),
        blockReason: customer.blockReason || "",
        recentBookings: Array.isArray(customer.recentBookings)
          ? customer.recentBookings
          : [],
      })),
    [customers]
  );

  const handleCustomerSearch = () => {
    const query = customerSearchQuery.trim();
    setCustomerAppliedQuery(query);
    setCustomersPage(1);
  };

  const handleToggleCustomer = (key) => {
    setExpandedCustomerKey((prev) => (prev === key ? null : key));
  };

  const handleCustomerReasonChange = (key, value) => {
    setCustomerBlockReasons((prev) => ({ ...prev, [key]: value }));
  };

  const handleToggleBlockCustomer = async (customer) => {
    if (!customer) return;
    setCustomerActionId(customer.key);
    try {
      if (customer.isBlocked) {
        await unblockAdminCustomer(customer.email);
        setCustomers((prev) =>
          prev.map((item) =>
            item.key === customer.key ? { ...item, isBlocked: false, blockReason: "" } : item
          )
        );
        setCustomerBlockReasons((prev) => ({ ...prev, [customer.key]: "" }));
        showFeedback(`${customer.email} unblocked.`, "info");
      } else {
        const reason = customerBlockReasons[customer.key] || "";
        await blockAdminCustomer({
          email: customer.email,
          phone: customer.phone,
          reason: reason || null,
        });
        setCustomers((prev) =>
          prev.map((item) =>
            item.key === customer.key ? { ...item, isBlocked: true, blockReason: reason } : item
          )
        );
        showFeedback(`${customer.email} blocked.`, "warning");
      }
      await refreshAudit({ silent: true });
    } catch (error) {
      showFeedback(error?.message || "Failed to update customer status.", "warning");
    } finally {
      setCustomerActionId(null);
    }
  };

  const handleSaveBlockReason = async (customer) => {
    if (!customer) return;
    setCustomerActionId(customer.key);
    try {
      const reason = customerBlockReasons[customer.key];
      await blockAdminCustomer({
        email: customer.email,
        phone: customer.phone,
        reason: reason || null,
      });
      setCustomers((prev) =>
        prev.map((item) =>
          item.key === customer.key ? { ...item, blockReason: reason } : item
        )
      );
      await refreshAudit({ silent: true });
      showFeedback("Block reason saved.", "success");
    } catch (error) {
      showFeedback(error?.message || "Failed to save block reason.", "warning");
    } finally {
      setCustomerActionId(null);
    }
  };

  const handleCustomerManualBooking = (customer) => {
    if (!customer) return;
    showFeedback("Manual booking creation is coming soon.", "info");
  };

  const handleResendCustomerConfirmation = (customer) => {
    if (!customer) return;
    showFeedback("Resend confirmations is coming soon.", "info");
  };

  return {
    customers,
    customersTotal,
    customersPage,
    setCustomersPage,
    customersPageSize,
    customersLoading,
    customersError,
    customerSearchQuery,
    setCustomerSearchQuery,
    customerSortBy,
    setCustomerSortBy,
    customerSortDir,
    setCustomerSortDir,
    expandedCustomerKey,
    customerActionId,
    customerBlockReasons,
    customerProfiles,
    totalCustomerPages,
    handleCustomerSearch,
    handleToggleCustomer,
    handleCustomerReasonChange,
    handleToggleBlockCustomer,
    handleSaveBlockReason,
    handleCustomerManualBooking,
    handleResendCustomerConfirmation,
  };
};

export default useAdminCustomers;
